import axios from 'axios';

// Pick the API base URL: explicit env var wins; otherwise fall back to
// localhost during development (CRA sets NODE_ENV automatically) and the
// production host for production builds.
const DEFAULT_API_URL =
  process.env.NODE_ENV === 'production'
    ? 'https://cv-screener.awtg.net/api'
    : 'http://localhost:8001/api';

export const API_URL = process.env.REACT_APP_API_URL || DEFAULT_API_URL;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Upload API
export const uploadFiles = async (formData) => {
  try {
    const response = await api.post('/upload/files', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

// Evaluation API
export const startEvaluation = async (batchId, forceReassess = false) => {
  try {
    const response = await api.post(`/evaluation/start/${batchId}`, null, {
      params: { force: forceReassess },
    });
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const getEvaluationStatus = async (batchId) => {
  try {
    const response = await api.get(`/evaluation/status/${batchId}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

// Reports API
export const getBatchReport = async (batchId) => {
  try {
    const response = await api.get(`/reports/batch/${batchId}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const exportReport = (batchId, format) => {
  window.open(`${API_URL}/reports/export/${batchId}/${format}`, '_blank');
};

export const getAllReports = async () => {
  const response = await fetch(`${API_URL}/reports/`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch reports');
  }
  
  return response.json();
};

export const deleteReport = async (batchId) => {
  const response = await fetch(`${API_URL}/reports/${batchId}`, {
    method: 'DELETE',
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete report');
  }
  
  return response.json();
};

// Candidate status and notes API
export const updateCandidateStatus = async (candidateId, statusData) => {
  const response = await fetch(`${API_URL}/reports/candidate/${candidateId}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(statusData),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update candidate status');
  }
  
  return response.json();
};

export const getCandidateDetails = async (candidateId) => {
  const response = await fetch(`${API_URL}/reports/candidate/${candidateId}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch candidate details');
  }
  
  return response.json();
};

// Email API
export const previewEmail = async (candidateId, customEmail = null, templateType = 'invitation') => {
  const response = await fetch(`${API_URL}/email/preview`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ 
      candidate_id: candidateId,
      custom_email: customEmail,
      template_type: templateType,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to preview email');
  }
  
  return response.json();
};

export const sendEmail = async (candidateId, recipientEmail, subject, body) => {
  const response = await fetch(`${API_URL}/email/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      candidate_id: candidateId,
      recipient_email: recipientEmail,
      subject: subject,
      body: body
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to send email');
  }
  
  return response.json();
};

export const getEmailLogs = async (candidateId) => {
  const response = await fetch(`${API_URL}/email/logs/${candidateId}`);
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to fetch email logs');
  }
  
  return response.json();
};

// Files API
export const getCandidateFileUrl = (candidateId) => {
  return `${API_URL}/files/${candidateId}`;
};

export const downloadCandidateFile = async (candidateId) => {
  const response = await fetch(`${API_URL}/files/download/${candidateId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to download CV file');
  }

  const contentDisposition = response.headers.get('content-disposition');
  let filename = 'cv.pdf';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="(.+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return { success: true, filename };
};

export const bulkDownloadCandidates = async (candidateIds) => {
  try {
    const response = await fetch(`${API_URL}/files/bulk-download`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(candidateIds),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to download files');
    }

    // Get filename from response headers
    const contentDisposition = response.headers.get('content-disposition');
    let filename = 'CV_Files.zip';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Create blob and download
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  } catch (error) {
    console.error('Bulk download failed:', error);
    throw error;
  }
};

// Roles API
export const getRoles = async () => {
  try {
    const response = await api.get('/roles');
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const getRole = async (roleId) => {
  try {
    const response = await api.get(`/roles/${roleId}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const createRole = async (roleData) => {
  try {
    const response = await api.post('/roles', roleData);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};

export const deleteRole = async (roleId) => {
  try {
    const response = await api.delete(`/roles/${roleId}`);
    return response.data;
  } catch (error) {
    throw error.response ? error.response.data : error;
  }
};
